<script setup lang="ts">
import {
  PDP_CTL_LABEL_LAYER_ID,
  type PdpEcommerceBlockCompleteTheLookCtaProps,
} from './PdpEcommerceBlockCompleteTheLookCta.props'

const props = withDefaults(
  defineProps<PdpEcommerceBlockCompleteTheLookCtaProps>(),
  {
    isCollapsed: false,
  }
)

const emit = defineEmits<{
  click: []
}>()

const { currentWebsite } = useWebsite()
const { isPdpCtlCtaExpanded } = useBrandsSettings()

const isPdpCtlCtaExpandedEnabled = computed(() =>
  isPdpCtlCtaExpanded(currentWebsite.value)
)

const isHovered = ref(false)
const isExpanded = computed(
  () =>
    (!props.isCollapsed || isHovered.value) && isPdpCtlCtaExpandedEnabled.value
)

// The label layer only exists on the client, teleporting during SSR would mismatch
const isMounted = useMounted()
</script>

<template>
  <button
    type="button"
    class="relative flex w-fit flex-col items-end text-left"
    :aria-label="label"
    @click="emit('click')"
    @mouseenter="isHovered = true"
    @mouseleave="isHovered = false"
    @focusin="isHovered = true"
    @focusout="isHovered = false"
  >
    <div
      aria-hidden="true"
      class="ctl-state flex flex-col items-end gap-2"
      :class="{ 'ctl-state-hidden -translate-y-1/2 opacity-0': !isExpanded }"
    >
      <!-- Keeps the label space clickable; the visible label is teleported -->
      <span class="text-book-6 invisible whitespace-nowrap uppercase">{{
        label
      }}</span>
      <ul class="flex gap-[2px]">
        <li
          v-for="item in items"
          :key="item.id"
          class="ctl-thumb border-primitives-grey-200 bg-primitives-off-white border"
        >
          <NuxtPicture
            provider="cloudinary"
            :src="item.imageUrl"
            sizes="80px"
            :img-attrs="{ class: 'h-full w-full object-contain' }"
          />
        </li>
      </ul>
    </div>
    <span
      aria-hidden="true"
      class="ctl-state text-book-8 border-primitives-grey-100 bg-neutral-white flex items-center gap-2 whitespace-nowrap border p-2 uppercase tracking-wider"
      :class="{ 'ctl-state-hidden translate-y-full opacity-0': isExpanded }"
    >
      <DefaultIconsAdd aria-hidden="true" class="h-4 w-4 shrink-0" />
      {{ label }}
    </span>
  </button>

  <Teleport v-if="isMounted" defer :to="`#${PDP_CTL_LABEL_LAYER_ID}`">
    <!-- Same box as the label + thumbs block, so the translate distance matches -->
    <div
      class="ctl-label ctl-state flex flex-col items-end gap-2"
      :class="{ '-translate-y-1/2 opacity-0': !isExpanded }"
    >
      <span class="text-book-6 text-neutral-white whitespace-nowrap uppercase">
        {{ label }}
      </span>
      <span class="ctl-thumb" />
    </div>
  </Teleport>
</template>

<style lang="scss" scoped>
/* 49x61 on a 1024px tall frame, scaled with the viewport height.
   Typography keeps its own tokens and never scales. */
.ctl-thumb {
  height: clamp(44px, 5.96dvh, 80px);
  width: auto;
  aspect-ratio: 4 / 5;
}

.ctl-label {
  position: absolute;
  right: 0;
  bottom: 0;
}

.ctl-state {
  transition:
    transform 600ms ease,
    opacity 600ms ease;
}

.ctl-state-hidden {
  position: absolute;
  right: 0;
  bottom: 0;
  pointer-events: none;
}
</style>
